import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { Callout } from '@blueprintjs/core'
import { DepopSpecificSelect } from './DepopSpecificSelect'
import { useDepopChannelFormContext } from '../../../../products_v4/listings/depop/DepopChannelFormContext'
import { Spinner } from '../../../../../components/Spinner'
import { useDepopSpecifics } from './useDepopSpecifics'

// Depop's own catalogue marks every specific optional, but brand_name is in
// DEPOP_REQUIRED_LISTING_DATA_FIELDS on the marketplace-service side and a listing without it is
// rejected, so we surface it as required regardless of what the catalogue says.
const ALWAYS_REQUIRED = ['brand']

// Depop ships no name_i18n for `colour`, so Depop::SpecificsParser falls back to the raw id and the
// label arrives lowercase and British. Only the label is overridden — the key stays `colour`,
// which is what Depop's payload expects.
const LABEL_OVERRIDES: Record<string, string> = {
  colour: 'Color'
}

export const Specifics: FC = () => {
  const { form, onListingChange } = useDepopChannelFormContext()
  const { specifics, valuesByName, loading, error } = useDepopSpecifics()

  const selected: Record<string, string> = useMemo(
    () => (form.specifics || []).reduce((acc, s) => ({ ...acc, [s.name]: s.value }), {}),
    [form.specifics]
  )

  const onSpecificChange = useCallback(
    (name: string, value: string) => {
      const next = { ...selected, [name]: value }
      const specificsList = Object.keys(next)
        .filter(key => next[key] !== null && next[key] !== undefined && next[key] !== '')
        .map(key => ({ name: key, value: next[key] }))

      onListingChange({ name: 'specifics', value: specificsList })
    },
    [selected, onListingChange]
  )

  if (error) return <Callout intent="warning">{error}</Callout>
  if (loading) return <Spinner />

  return (
    <>
      {specifics.map(specific => (
        <DepopSpecificSelect
          key={specific.name}
          specificName={LABEL_OVERRIDES[specific.name] || specific.displayName || specific.name}
          value={selected[specific.name] || null}
          options={valuesByName[specific.name] || []}
          required={specific.required || ALWAYS_REQUIRED.includes(specific.name)}
          onChange={v => onSpecificChange(specific.name, v)}
        />
      ))}
    </>
  )
}
